import { useOptimizationGroupData } from '@e2m-customer-portal/hooks';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { OptimizationGroupParametersType } from 'domains/optimization-group/types';
import { useTranslation } from 'react-i18next';
import { OptimizationParametersButton } from './csv-download-button/OptimizationParametersButton';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { BatteryPowerType } from './feed-in-chart/utils';

interface OptimizationGroupMetaInfoProps {
  id: string;
  setBatteriesPower: Dispatch<SetStateAction<Map<string, BatteryPowerType>>>;
}

export function OptimizationGroupMetaInfo({ id, setBatteriesPower }: Readonly<OptimizationGroupMetaInfoProps>) {
  const [t] = useTranslation();

  const { data } = useOptimizationGroupData(id);
  const optimizationGroupData: OptimizationGroupParametersType = data?.json && JSON.parse(data.json);

  const getSingleBattery = (optimizationGroupData: OptimizationGroupParametersType) =>
    optimizationGroupData?.battery ? [optimizationGroupData.battery] : undefined;

  const batteries = optimizationGroupData?.batteries
    ? Object.values(optimizationGroupData.batteries)
    : getSingleBattery(optimizationGroupData);

  useEffect(() => {
    if (batteries) {
      setBatteriesPower(new Map());
      for (const battery of batteries) {
        if (battery.power_charge && battery.power_discharge)
          setBatteriesPower((batteriesPower) =>
            batteriesPower.set(`${battery.device_number}`, {
              power_charge: -battery.power_charge,
              power_discharge: battery.power_discharge,
            }),
          );
      }
    }
  }, [data]);

  if (!data) return;

  return (
    <>
      <div>
        <span className="w-max text-xs text-grey-400">{t('COPT_GROUP')}</span>
        <span className="w-max text-sm leading-4 font-semibold">{optimizationGroupData.name}</span>
      </div>
      <div>
        <span className="w-max text-xs text-grey-400">{t('COPT_STATUS')}</span>
        <span className="w-max text-sm leading-4 font-semibold">{optimizationGroupData.status}</span>
      </div>
      <div>
        <span className="w-max text-xs text-grey-400">{t('COPT_VALID_FROM_TIMESTAMP')}</span>
        <span className="w-max text-sm leading-4 font-semibold">
          {format(optimizationGroupData.valid_from_timestamp, 'Pp', { locale: de })}
        </span>
      </div>
      <div>
        <span className="w-max text-xs text-grey-400">{t('COPT_ASSET_TYPE')}</span>
        <span className="w-max text-sm leading-4 font-semibold">{optimizationGroupData.asset_type}</span>
      </div>
      <div className="flex grow items-end">
        <div className="w-auto">{id && <OptimizationParametersButton id={id} data={optimizationGroupData} />}</div>
      </div>
    </>
  );
}
